/**
 * Cloister Configuration
 *
 * Loads and manages Cloister configuration from ~/.overdeck/cloister.toml
 */

import { readFileSync, writeFileSync, existsSync, mkdirSync } from 'fs';
import { mkdir, readFile, writeFile } from 'fs/promises';
import { parse, stringify } from '@iarna/toml';
import { join } from 'path';
import { Effect } from 'effect';
import { ConfigError, FsError } from '../errors.js';
import { OVERDECK_HOME } from '../paths.js';
import type { TieredExecutionConfig } from '../agents/tier-table.js';

const CLOISTER_CONFIG_FILE = join(OVERDECK_HOME, 'cloister.toml');

/**
 * Health threshold configuration (in minutes)
 */
export interface HealthThresholds {
  stale: number;
  warning: number;
  stuck: number;
}

/**
 * Automatic action configuration
 */
export interface AutoActions {
  poke_on_warning: boolean;
  poke_on_stuck: boolean;   // Poke agents idle > stuck threshold (default: true)
  kill_on_stuck: boolean;
  restart_on_kill: boolean;
  /** Minimum ms between pokes for the same agent. Prevents spam on repeated health checks. Default: 30 min */
  poke_cooldown_ms: number;
}

export interface StuckRemediationConfig {
  enabled: boolean;
  stage1_minutes: number;
  stage2_minutes: number;
  stage3_minutes: number;
  flywheel_stage1_minutes: number;
  flywheel_stage2_minutes: number;
  flywheel_stage3_minutes: number;
}

/**
 * Monitoring configuration
 */
export interface MonitoringConfig {
  check_interval: number; // seconds between health checks
  heartbeat_sources: ('jsonl_mtime' | 'tmux_activity' | 'git_activity' | 'active_heartbeat')[];
}

/**
 * Concurrency limits for deacon auto-resume / auto-dispatch (PAN-1665).
 *
 * These are *gates on starting new work* — the deacon never kills a running agent
 * to satisfy them. If the system is already over a limit (e.g. after a forced
 * `pan start`, or a backlog at unfreeze), the deacon simply stops resuming/
 * dispatching until natural attrition brings the count down. To forcibly trim an
 * over-limit system back to the cap, the operator uses the explicit emergency
 * brake — it is never automatic.
 */
export interface ConcurrencyConfig {
  /** Max concurrently-running *work* agents the deacon will resume/spawn up to. */
  max_work_agents: number;
  /**
   * Slots kept free above `max_work_agents` for advancing roles (review/test/ship)
   * so the pipeline can always drain even when work is at its cap. The overall
   * ceiling for any auto-dispatch is `max_work_agents + reserved_advancing_slots`.
   */
  reserved_advancing_slots: number;
  /**
   * Dedicated swarm-slot reserve, isolated from `max_work_agents` and
   * `reserved_advancing_slots` (PAN-2212). Swarm slot dispatch draws only from this
   * reserve, so a busy pipeline never starves the swarm — and swarm slots never
   * starve review/test in reverse. Default 3.
   */
  reserved_swarm_slots: number;
  /**
   * When true, operator-started work agents (no flywheelRunId) are exempt from
   * the emergency brake/governor reaping so the operator's deliberate spawns are
   * not trimmed to satisfy the cap. Defaults to true (PAN-1812).
   */
  exempt_operator_started?: boolean;
  /**
   * PAN-2504: memory-DRIVEN work-agent admission. When true, the work-agent
   * ceiling is derived from live free memory — admit until the next agent's
   * estimated footprint would breach the governor's SOFT reserve — instead of
   * the fixed `max_work_agents` count. This lets a big box fill toward its
   * memory budget rather than idling at a hardcoded 6. Defaults to false
   * (opt-in): unset installs keep the count-based cap. Requires the memory
   * governor to have published a cached verdict; falls back to the count cap
   * until then.
   */
  memory_driven?: boolean;
  /**
   * Safety upper bound on concurrent work agents when `memory_driven` is on —
   * caps the memory-derived ceiling so a mis-estimated footprint can't march the
   * box to hundreds of agents. Default 24.
   */
  memory_driven_max_work_agents?: number;
  /**
   * Per-work-agent RSS estimate (GB) used for the memory-driven budget:
   * additional slots = floor((availableBytes - softReserve) / work_footprint_gb).
   * Conservative by design (over-estimating admits fewer). Default 2, matching
   * `resources.governorFootprintDefaultWorkGb`.
   */
  work_footprint_gb?: number;
  /**
   * PAN-2507: preemptive pipeline scheduler. When true, a blocked advancing
   * (review/test/ship) dispatch may YIELD an idle work agent — pause it
   * (resumable, session preserved) to free the slot/memory, dispatch the
   * advancing role, and auto-resume the yielded agent oldest-first once
   * capacity returns. Defaults to false (opt-in): unset installs keep the
   * static defer-until-attrition behavior at every dispatch site.
   */
  preemption?: boolean;
  /**
   * PAN-2507: anti-thrash cap on how many work agents may be in the yielded
   * (scheduler-paused) state at once. Default 3.
   */
  max_yielded?: number;
  /**
   * PAN-2507: anti-thrash cooldown (seconds) — an agent resumed from a yield
   * may not be re-yielded until this many seconds have elapsed since its
   * resume. Default 600.
   */
  yield_cooldown_secs?: number;
}

export type SwarmInferCompletionMode = 'off' | 'nudge' | 'auto';

export interface SwarmConfig {
  infer_completion: SwarmInferCompletionMode;
}

/**
 * Startup configuration
 */
export interface StartupConfig {
  auto_start: boolean; // Start Cloister when dashboard starts
  reconciliation_grace_secs: number; // Boot reconciliation grace window before default resume
  reconciliation_max_candidate_age_secs: number; // Max age for stopped agents to appear in boot reconciliation
}

/**
 * Notification configuration (future feature)
 */
export interface NotificationConfig {
  slack_webhook?: string;
  email?: string;
}

/**
 * Specialist agent configuration
 */
export interface SpecialistConfig {
  enabled: boolean;
  auto_wake: boolean;
}

/**
 * Test agent specific configuration
 */
export interface TestAgentConfig extends SpecialistConfig {
  test_command?: string; // Optional test command override (e.g., "npm test", "pytest", etc.)
}

/**
 * Configuration for a single reviewer agent in the parallel review flow.
 */
export interface ReviewAgentConfig {
  /** Unique name / role identifier (e.g. 'correctness', 'security', 'performance') */
  name: string;
  /** Optional model override (e.g. 'claude-opus-4-6'). Falls back to work-type routing. */
  model?: string;
  /** Focus areas for this reviewer (informational, passed as context) */
  focus?: string[];
  /** Set to false to skip this reviewer. Defaults to true. */
  enabled?: boolean;
}

/**
 * All specialist agents configuration
 */
export interface SpecialistsConfig {
  merge_agent?: SpecialistConfig;
  review_agent?: SpecialistConfig;
  test_agent?: TestAgentConfig;
  inspect_agent?: SpecialistConfig;
  uat_agent?: SpecialistConfig;
  /** User-configurable list of parallel reviewer agents. Absent ⇒ 3 built-in defaults. */
  review_agents?: ReviewAgentConfig[];
}

/**
 * Model selection configuration
 */
export interface ModelSelectionConfig {
  default_model: 'opus' | 'sonnet' | 'haiku';
  complexity_routing: {
    trivial: 'opus' | 'sonnet' | 'haiku';
    simple: 'opus' | 'sonnet' | 'haiku';
    medium: 'opus' | 'sonnet' | 'haiku';
    complex: 'opus' | 'sonnet' | 'haiku';
    expert: 'opus' | 'sonnet' | 'haiku';
  };
  specialist_models: {
    merge_agent?: 'opus' | 'sonnet' | 'haiku';
    review_agent?: 'opus' | 'sonnet' | 'haiku';
    test_agent?: 'opus' | 'sonnet' | 'haiku';
    inspect_agent?: 'opus' | 'sonnet' | 'haiku';
    uat_agent?: 'opus' | 'sonnet' | 'haiku';
  };
  /**
   * PAN-636 legacy coding-agent harness override. PAN-1787 treats this as a
   * deprecated alias for roles.<role>.harness; roles.<role>.harness wins when
   * both are set. Absent keys are normal for older config files.
   */
  specialist_harnesses?: {
    merge_agent?: 'claude-code' | 'pi';
    review_agent?: 'claude-code' | 'pi';
    test_agent?: 'claude-code' | 'pi';
    inspect_agent?: 'claude-code' | 'pi';
    uat_agent?: 'claude-code' | 'pi';
  };
}

/**
 * Handoff trigger configuration
 */
export interface HandoffTriggersConfig {
  stuck_escalation?: {
    enabled: boolean;
    haiku_to_sonnet_minutes: number;
    sonnet_to_opus_minutes: number;
  };
  test_failure?: {
    enabled: boolean;
    from_model: 'opus' | 'sonnet' | 'haiku';
    to_model: 'opus' | 'sonnet' | 'haiku';
    trigger_on: 'any_failure' | '2_consecutive';
  };
  implementation_complete?: {
    enabled: boolean;
    to_specialist: string; // e.g., 'test-agent'
  };
}

/**
 * Handoff configuration
 */
export interface HandoffConfig {
  auto_triggers: HandoffTriggersConfig;
}

/**
 * Cost tracking configuration
 */
export interface CostTrackingConfig {
  display_enabled: boolean;
  log_to_jsonl: boolean;
}

/**
 * Auto-restart configuration
 */
export interface AutoRestartConfig {
  enabled: boolean;
  max_retries: number;
  backoff_seconds: number[]; // Array of backoff delays (e.g., [30, 60, 120])
}

/**
 * Cost limits configuration
 */
export interface CostLimitsConfig {
  per_agent_usd: number;
  per_issue_usd: number;
  daily_total_usd: number;
  alert_threshold: number; // Fraction (0.0-1.0) at which to start alerting
}

/**
 * Retention policy configuration
 */
export interface RetentionConfig {
  /**
   * Days to keep work/planning agent state dirs after their session ends.
   * Default 7. Event-driven cleanup (postMergeLifecycle, executeCloseOut)
   * deletes state at the moment it becomes useless; this retention is only
   * a safety net for cases where those events didn't fire.
   */
  agent_state_days: number;
  /**
   * Days to keep reviewer state dirs (review-* prefix). Default 1.
   * runParallelReview Phase 6 deletes reviewer state immediately after the
   * review posts; this retention catches crashed-mid-cleanup edge cases.
   */
  reviewer_state_days?: number;
  health_staleness_hours: number; // Hours before hiding stale agents in health API (default: 24)
}

export interface CloseOutConfig {
  remove_workspace: boolean;
  delete_feature_branch: boolean;
  auto: boolean;
  auto_delay_minutes: number;
}

export interface OrphanProposedReconcilerConfig {
  enabled: boolean;
  minAttemptIntervalMs: number;
}

/**
 * Complete Cloister configuration
 */
export interface CloisterConfig {
  startup: StartupConfig;
  thresholds: HealthThresholds;
  auto_actions: AutoActions;
  stuck_remediation?: StuckRemediationConfig;
  monitoring: MonitoringConfig;
  concurrency?: ConcurrencyConfig;
  swarm?: SwarmConfig;
  notifications?: NotificationConfig;
  specialists?: SpecialistsConfig;
  model_selection?: ModelSelectionConfig;
  tiered_execution?: TieredExecutionConfig;
  handoffs?: HandoffConfig;
  cost_tracking?: CostTrackingConfig;
  auto_restart?: AutoRestartConfig;
  cost_limits?: CostLimitsConfig;
  retention?: RetentionConfig;
  close_out?: CloseOutConfig;
  orphanProposedReconciler?: OrphanProposedReconcilerConfig;
}

/**
 * Default Cloister configuration
 */
export const DEFAULT_CLOISTER_CONFIG: CloisterConfig = {
  startup: {
    auto_start: true,
    reconciliation_grace_secs: 120,
    reconciliation_max_candidate_age_secs: 240,
  },
  thresholds: {
    stale: 5,
    warning: 15,
    stuck: 30,
  },
  auto_actions: {
    poke_on_warning: true,
    poke_on_stuck: true,   // Poke agents that have been idle > stuck threshold
    kill_on_stuck: false,  // Manual by default for safety
    restart_on_kill: false,
    poke_cooldown_ms: 30 * 60 * 1000, // 30 min between pokes for the same agent
  },
  stuck_remediation: {
    enabled: true,
    stage1_minutes: 20,
    stage2_minutes: 45,
    stage3_minutes: 90,
    flywheel_stage1_minutes: 20,
    flywheel_stage2_minutes: 24,
    flywheel_stage3_minutes: 28,
  },
  monitoring: {
    check_interval: 60, // 1 minute
    heartbeat_sources: ['jsonl_mtime', 'tmux_activity', 'git_activity'],
  },
  concurrency: {
    max_work_agents: 6,
    reserved_advancing_slots: 3,
    reserved_swarm_slots: 3,
    exempt_operator_started: true,
    memory_driven: false,
    memory_driven_max_work_agents: 24,
    work_footprint_gb: 2,
    preemption: false,
    max_yielded: 3,
    yield_cooldown_secs: 600,
  },
  swarm: {
    infer_completion: 'auto',
  },
  notifications: {
    slack_webhook: undefined,
    email: undefined,
  },
  specialists: {
    merge_agent: {
      enabled: true,
      auto_wake: false, // Only wake on explicit "Approve & Merge" click
    },
    review_agent: {
      enabled: true,
      auto_wake: false, // Woken by the verification pipeline, not ad hoc polling
    },
    test_agent: {
      enabled: true,
      auto_wake: true,
    },
    inspect_agent: {
      enabled: true,
      auto_wake: false, // Triggered explicitly per bead via pan inspect
    },
    uat_agent: {
      enabled: true,
      auto_wake: true,
    },
  },
  model_selection: {
    default_model: 'sonnet',
    complexity_routing: {
      trivial: 'haiku',
      simple: 'haiku',
      medium: 'sonnet',
      complex: 'sonnet',
      expert: 'opus',
    },
    specialist_models: {
      // PAN-754: no hardcoded defaults. User config.yaml role settings are authoritative.
      // Resolution falls through to role model config, then to the global fallback model.
    },
    specialist_harnesses: {
      // PAN-1787/PAN-1842: removed. Harness resolution for all spawn paths
      // now flows through resolveHarness(), which consults explicit, role,
      // providerHarnesses, and built-in provider defaults in order.
    },
  },
  tiered_execution: {
    enabled: false,
    tiers: {},
    supervisor: undefined,
    replay_threshold: 0.5,
  },
  handoffs: {
    auto_triggers: {
      stuck_escalation: {
        enabled: true,
        haiku_to_sonnet_minutes: 10,
        sonnet_to_opus_minutes: 20,
      },
      test_failure: {
        enabled: true,
        from_model: 'haiku',
        to_model: 'sonnet',
        trigger_on: 'any_failure',
      },
      implementation_complete: {
        enabled: true, // Auto-handoff to test-agent when implementation done
        to_specialist: 'test-agent',
      },
    },
  },
  cost_tracking: {
    display_enabled: true,
    log_to_jsonl: true,
  },
  auto_restart: {
    enabled: true,
    max_retries: 3,
    backoff_seconds: [30, 60, 120], // 30s, 1m, 2m
  },
  cost_limits: {
    per_agent_usd: 10.0,
    per_issue_usd: 25.0,
    daily_total_usd: 100.0,
    alert_threshold: 0.8, // Alert at 80%
  },
  retention: {
    agent_state_days: 7,
    reviewer_state_days: 1,
    health_staleness_hours: 24,
  },
  close_out: {
    remove_workspace: false,
    delete_feature_branch: false,
    auto: false,
    auto_delay_minutes: 60,
  },
  orphanProposedReconciler: {
    enabled: true,
    minAttemptIntervalMs: 5 * 60 * 1000,
  },
};

/**
 * Deep merge utility that recursively merges objects.
 * - Recursively merges nested objects
 * - Arrays in overrides replace defaults (not concatenated)
 * - User values take precedence over defaults
 */
function deepMerge<T extends object>(defaults: T, overrides: Partial<T>): T {
  const result = { ...defaults };

  for (const key of Object.keys(overrides) as (keyof T)[]) {
    const defaultVal = defaults[key];
    const overrideVal = overrides[key];

    // Skip undefined values in overrides
    if (overrideVal === undefined) continue;

    // Deep merge if both values are non-array objects
    if (
      typeof defaultVal === 'object' &&
      defaultVal !== null &&
      !Array.isArray(defaultVal) &&
      typeof overrideVal === 'object' &&
      overrideVal !== null &&
      !Array.isArray(overrideVal)
    ) {
      result[key] = deepMerge(defaultVal as any, overrideVal as any);
    } else {
      // Direct override for primitives and arrays
      result[key] = overrideVal as T[keyof T];
    }
  }

  return result;
}

/**
 * Load Cloister configuration
 *
 * Reads from ~/.overdeck/cloister.toml and merges with defaults.
 * Creates default config file if it doesn't exist.
 */
export function loadCloisterConfigSync(): CloisterConfig {
  // Ensure overdeck home exists
  if (!existsSync(OVERDECK_HOME)) {
    mkdirSync(OVERDECK_HOME, { recursive: true });
  }

  let config = DEFAULT_CLOISTER_CONFIG;

  // If config file doesn't exist, create it with defaults
  if (!existsSync(CLOISTER_CONFIG_FILE)) {
    saveCloisterConfigSync(DEFAULT_CLOISTER_CONFIG);
  } else {
    try {
      const content = readFileSync(CLOISTER_CONFIG_FILE, 'utf-8');
      const parsed = parse(content) as unknown as Partial<CloisterConfig>;

      // Deep merge with defaults
      config = deepMerge(DEFAULT_CLOISTER_CONFIG, parsed);
    } catch (error) {
      console.error('Failed to load Cloister config:', error);
      console.error('Using default configuration');
      config = DEFAULT_CLOISTER_CONFIG;
    }
  }

  return config;
}


/**
 * Save Cloister configuration
 *
 * Writes configuration to ~/.overdeck/cloister.toml
 */
export function saveCloisterConfigSync(config: CloisterConfig): void {
  // Ensure overdeck home exists
  if (!existsSync(OVERDECK_HOME)) {
    mkdirSync(OVERDECK_HOME, { recursive: true });
  }

  try {
    const content = stringify(config as any);
    writeFileSync(CLOISTER_CONFIG_FILE, content, 'utf-8');
  } catch (error) {
    console.error('Failed to save Cloister config:', error);
    throw error;
  }
}

/**
 * Update Cloister configuration
 *
 * Merges partial config updates with existing config.
 */
export function updateCloisterConfigSync(updates: Partial<CloisterConfig>): CloisterConfig {
  const current = loadCloisterConfigSync();
  const updated = deepMerge(current, updates);
  saveCloisterConfigSync(updated);
  return updated;
}

/**
 * Get the path to the Cloister config file
 */
export function getCloisterConfigPath(): string {
  return CLOISTER_CONFIG_FILE;
}

/**
 * Check if Cloister should auto-start
 */
export function shouldAutoStart(): boolean {
  const config = loadCloisterConfigSync();
  return config.startup.auto_start;
}

/**
 * Get health thresholds in milliseconds
 */
export function getHealthThresholdsMs(): {
  stale: number;
  warning: number;
  stuck: number;
} {
  const config = loadCloisterConfigSync();
  return {
    stale: config.thresholds.stale * 60 * 1000,
    warning: config.thresholds.warning * 60 * 1000,
    stuck: config.thresholds.stuck * 60 * 1000,
  };
}

// ─── Effect variants (PAN-1249) ───────────────────────────────────────────────
//
// Additive Effect-channel variants of the config helpers above. The sync
// variants are preserved so existing callers (CLI scripts, top-level module
// initialization) do not have to migrate; new Effect-based callers can compose
// these directly without `Effect.runSync` round-tripping.

/** Effect variant of `loadCloisterConfig`. Falls back to defaults on read/parse failures. */
export const loadCloisterConfig = (): Effect.Effect<CloisterConfig, FsError | ConfigError> =>
  Effect.gen(function* () {
    yield* Effect.tryPromise({
      try: () => mkdir(OVERDECK_HOME, { recursive: true }),
      catch: (cause) => new FsError({ path: OVERDECK_HOME, operation: 'mkdir', cause }),
    });

    let config: CloisterConfig = DEFAULT_CLOISTER_CONFIG;

    if (!existsSync(CLOISTER_CONFIG_FILE)) {
      yield* saveCloisterConfig(DEFAULT_CLOISTER_CONFIG);
    } else {
      const content: string | null = yield* Effect.tryPromise({
        try: () => readFile(CLOISTER_CONFIG_FILE, 'utf-8'),
        catch: (cause) => new FsError({ path: CLOISTER_CONFIG_FILE, operation: 'readFile', cause }),
      }).pipe(
        Effect.catch((err: FsError) => {
          console.error('Failed to load Cloister config:', err);
          console.error('Using default configuration');
          return Effect.succeed<string | null>(null);
        }),
      );

      if (content !== null) {
        try {
          const parsed = parse(content) as unknown as Partial<CloisterConfig>;
          config = deepMerge(DEFAULT_CLOISTER_CONFIG, parsed);
        } catch (error) {
          console.error('Failed to parse Cloister config:', error);
          console.error('Using default configuration');
          config = DEFAULT_CLOISTER_CONFIG;
        }
      }
    }

    return config;
  });

/** Effect variant of `saveCloisterConfig`. */
export const saveCloisterConfig = (config: CloisterConfig): Effect.Effect<void, FsError | ConfigError> =>
  Effect.gen(function* () {
    yield* Effect.tryPromise({
      try: () => mkdir(OVERDECK_HOME, { recursive: true }),
      catch: (cause) => new FsError({ path: OVERDECK_HOME, operation: 'mkdir', cause }),
    });

    const content = yield* Effect.try({
      try: () => stringify(config as any),
      catch: (cause) => new ConfigError({ message: 'Failed to serialize Cloister config', cause }),
    });

    yield* Effect.tryPromise({
      try: () => writeFile(CLOISTER_CONFIG_FILE, content, 'utf-8'),
      catch: (cause) => new FsError({ path: CLOISTER_CONFIG_FILE, operation: 'writeFile', cause }),
    });
  });

/** Effect variant of `updateCloisterConfig`. */
export const updateCloisterConfig = (
  updates: Partial<CloisterConfig>,
): Effect.Effect<CloisterConfig, FsError | ConfigError> =>
  Effect.gen(function* () {
    const current = yield* loadCloisterConfig();
    const updated = deepMerge(current, updates);
    yield* saveCloisterConfig(updated);
    return updated;
  });
